import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { customersAPI, reportsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, Eye, Pencil, Trash2, Phone, Mail, MapPin, CalendarIcon, AlertTriangle, FileSignature } from 'lucide-react';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const emptyCustomer = {
  contact_name: '',
  email: '',
  phone: '',
  street_address: '',
  city: '',
  state: '',
  zip_code: '',
  billing_street: '',
  billing_city: '',
  billing_state: '',
  billing_zip: '',
  preferred_communication: 'phone',
  preferred_days: [],
  service_frequency: 'weekly',
  contract_start_date: '',
  contract_end_date: '',
  cost_per_service: '',
  monthly_rate: '',
  billing_plan: 'per-cut',
  height_preference: '',
  work_notes: '',
  preference_notes: '',
  customer_notes: '',  // Visible to customer
  status: 'active'
};

// Phone formatting helper
const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  }
  return phone;
};

// Helper to check contract status
const getContractStatus = (customer) => {
  if (!customer.contract_end_date) return null;
  
  const now = new Date();
  const endDate = new Date(customer.contract_end_date);
  const daysUntil = Math.floor((endDate - now) / (1000 * 60 * 60 * 24));
  
  if (daysUntil < 0) {
    return { type: 'expired', label: 'Contract Expired', days: Math.abs(daysUntil), color: 'bg-red-100 text-red-700' };
  } else if (daysUntil <= 30) {
    return { type: 'expiring', label: 'Expiring Soon', days: daysUntil, color: 'bg-orange-100 text-orange-700' };
  }
  return null;
};

export default function Customers() {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState(emptyCustomer);
  const [sameBillingAddress, setSameBillingAddress] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, [statusFilter]);

  const fetchCustomers = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await customersAPI.getAll(params);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_name || !formData.phone || !formData.street_address || !formData.city || !formData.state || !formData.zip_code || !formData.cost_per_service) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const data = {
        ...formData,
        cost_per_service: parseFloat(formData.cost_per_service),
        monthly_rate: formData.monthly_rate ? parseFloat(formData.monthly_rate) : null,
        contract_start_date: formData.contract_start_date || null,
        contract_end_date: formData.contract_end_date || null,
        // If same billing address, copy service address
        billing_street: sameBillingAddress ? formData.street_address : formData.billing_street,
        billing_city: sameBillingAddress ? formData.city : formData.billing_city,
        billing_state: sameBillingAddress ? formData.state : formData.billing_state,
        billing_zip: sameBillingAddress ? formData.zip_code : formData.billing_zip,
      };

      if (editingCustomer) {
        await customersAPI.update(editingCustomer.id, data);
        toast.success('Customer updated');
      } else {
        await customersAPI.create(data);
        toast.success('Customer created');
      }
      
      setDialogOpen(false);
      setEditingCustomer(null);
      setFormData(emptyCustomer);
      setSameBillingAddress(true);
      fetchCustomers();
    } catch (error) {
      console.error('Save error:', error);
      // Handle different error formats
      let errorMessage = 'Failed to save customer';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          errorMessage = error.response.data.detail.map(e => e.msg || e.message || 'Validation error').join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    const isSameBilling = 
      customer.billing_street === customer.street_address &&
      customer.billing_city === customer.city;
    setSameBillingAddress(isSameBilling);
    setFormData({
      ...customer,
      cost_per_service: customer.cost_per_service?.toString() || '',
      monthly_rate: customer.monthly_rate?.toString() || '',
      contract_start_date: customer.contract_start_date || '',
      contract_end_date: customer.contract_end_date || ''
    });
    setDialogOpen(true);
  };

  const handleStatusToggle = async (customer, newStatus) => {
    try {
      await customersAPI.update(customer.id, { status: newStatus });
      toast.success(`Customer marked as ${newStatus}`);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await customersAPI.delete(id);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day)
        ? prev.preferred_days.filter(d => d !== day)
        : [...prev.preferred_days, day]
    }));
  };

  const filteredCustomers = customers.filter(c => 
    c.contact_name.toLowerCase().includes(search.toLowerCase()) ||
    c.phone.includes(search) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getFullAddress = (customer) => {
    // Handle both new split format and legacy physical_address format
    if (customer.street_address && customer.city && customer.state) {
      return `${customer.street_address}, ${customer.city}, ${customer.state} ${customer.zip_code || ''}`;
    }
    // Fallback to legacy format
    return customer.physical_address || 'No address';
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-500 mt-1">Manage your customer base</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingCustomer(null);
            setFormData(emptyCustomer);
            setSameBillingAddress(true);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-b pb-2">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Name *</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      placeholder="John Doe"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(318) 222-3333"
                      data-testid="customer-phone-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Any format accepted - will be standardized</p>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label>Preferred Communication</Label>
                    <Select value={formData.preferred_communication} onValueChange={(v) => setFormData({...formData, preferred_communication: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Service Address */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lime-500 border-b border-gray-600 pb-2">Service Address</h3>
                <div>
                  <Label>Street Address *</Label>
                  <Input
                    value={formData.street_address}
                    onChange={(e) => setFormData({...formData, street_address: e.target.value})}
                    placeholder="123 Main Street"
                    data-testid="customer-street-input"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Label>City *</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="City"
                      data-testid="customer-city-input"
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <Select value={formData.state} onValueChange={(v) => setFormData({...formData, state: v})}>
                      <SelectTrigger data-testid="customer-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                      <SelectContent>
                        {US_STATES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>ZIP Code *</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                      placeholder="12345"
                      data-testid="customer-zip-input"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-gray-600 pb-2">
                  <h3 className="font-semibold text-lime-500">Billing Address</h3>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <Checkbox 
                      checked={sameBillingAddress} 
                      onCheckedChange={(checked) => setSameBillingAddress(checked)}
                    />
                    Same as service address
                  </label>
                </div>
                
                {!sameBillingAddress && (
                  <>
                    <div>
                      <Label>Street Address</Label>
                      <Input
                        value={formData.billing_street}
                        onChange={(e) => setFormData({...formData, billing_street: e.target.value})}
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2">
                        <Label>City</Label>
                        <Input
                          value={formData.billing_city}
                          onChange={(e) => setFormData({...formData, billing_city: e.target.value})}
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <Label>State</Label>
                        <Select value={formData.billing_state} onValueChange={(v) => setFormData({...formData, billing_state: v})}>
                          <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>ZIP Code</Label>
                        <Input
                          value={formData.billing_zip}
                          onChange={(e) => setFormData({...formData, billing_zip: e.target.value})}
                          placeholder="12345"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Service Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lime-500 border-b border-gray-600 pb-2">Service Details</h3>
                <div>
                  <Label>Preferred Service Days</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {DAYS_OF_WEEK.map(day => (
                      <label 
                        key={day}
                        className={`px-3 py-1 rounded-full border cursor-pointer text-sm transition-colors ${
                          formData.preferred_days.includes(day)
                            ? 'bg-lime-500 border-lime-500 text-black'
                            : 'border-gray-500 text-gray-300 hover:border-lime-500'
                        }`}
                      >
                        <Checkbox
                          checked={formData.preferred_days.includes(day)}
                          onCheckedChange={() => toggleDay(day)}
                          className="hidden"
                        />
                        {day.slice(0, 3)}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Service Frequency</Label>
                    <Select value={formData.service_frequency} onValueChange={(v) => setFormData({...formData, service_frequency: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Every Two Weeks</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="one-time">One-Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Customer Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Contract Start Date</Label>
                    <Input
                      type="date"
                      value={formData.contract_start_date}
                      onChange={(e) => setFormData({...formData, contract_start_date: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Label>Contract End Date</Label>
                    <Input
                      type="date"
                      value={formData.contract_end_date}
                      onChange={(e) => setFormData({...formData, contract_end_date: e.target.value})}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Billing */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-b pb-2">Billing</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Billing Plan</Label>
                    <Select value={formData.billing_plan} onValueChange={(v) => setFormData({...formData, billing_plan: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per-cut">Per Cut</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cost Per Service *</Label>
                    <Input
                      type="number"
                      value={formData.cost_per_service}
                      onChange={(e) => setFormData({...formData, cost_per_service: e.target.value})}
                      placeholder="50"
                      data-testid="customer-cost-input"
                      className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <div>
                    <Label>Monthly Rate</Label>
                    <Input
                      type="number"
                      value={formData.monthly_rate}
                      onChange={(e) => setFormData({...formData, monthly_rate: e.target.value})}
                      placeholder="200"
                      className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lime-500 border-b border-gray-600 pb-2">Notes & Preferences (Admin Only)</h3>
                <div>
                  <Label>Height Preference</Label>
                  <Input
                    value={formData.height_preference}
                    onChange={(e) => setFormData({...formData, height_preference: e.target.value})}
                    placeholder="e.g., 3 inches"
                  />
                </div>
                <div>
                  <Label>Work Site Notes</Label>
                  <Textarea
                    value={formData.work_notes}
                    onChange={(e) => setFormData({...formData, work_notes: e.target.value})}
                    placeholder="Notes about the property, obstacles, etc."
                  />
                </div>
                <div>
                  <Label>Customer Preferences</Label>
                  <Textarea
                    value={formData.preference_notes}
                    onChange={(e) => setFormData({...formData, preference_notes: e.target.value})}
                    placeholder="Special requests or preferences"
                  />
                </div>
              </div>

              {/* Customer-Visible Notes */}
              <div className="space-y-4">
                <h3 className="font-semibold text-blue-500 border-b border-gray-600 pb-2">
                  📝 Customer Notes (Visible to Customer)
                </h3>
                <div>
                  <Label className="text-blue-400">Notes for Customer</Label>
                  <p className="text-xs text-gray-400 mb-2">These notes will appear on invoices and contracts sent to the customer.</p>
                  <Textarea
                    value={formData.customer_notes}
                    onChange={(e) => setFormData({...formData, customer_notes: e.target.value})}
                    placeholder="Notes that will be visible to the customer..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-600">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="customer-submit-btn">
                  {editingCustomer ? 'Update Customer' : 'Add Customer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search customers..."
                className="pl-10"
                data-testid="customer-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customer List */}
      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse" />)}
        </div>
      ) : filteredCustomers.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <p className="text-gray-500">No customers found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCustomers.map((customer) => (
            <Card key={customer.id} className="border-0 shadow-sm" data-testid={`customer-card-${customer.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-gray-900">{customer.contact_name}</h3>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={customer.status === 'active'}
                          onCheckedChange={(checked) => handleStatusToggle(customer, checked ? 'active' : 'inactive')}
                          className="data-[state=checked]:bg-lime-500"
                        />
                        <span className={`text-xs ${customer.status === 'active' ? 'text-lime-600' : 'text-gray-500'}`}>
                          {customer.status}
                        </span>
                      </div>
                      {/* Contract Status Badge */}
                      {(() => {
                        const contractStatus = getContractStatus(customer);
                        if (contractStatus) {
                          return (
                            <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${contractStatus.color}`}>
                              {contractStatus.type === 'expired' ? (
                                <AlertTriangle className="w-3 h-3" />
                              ) : (
                                <FileSignature className="w-3 h-3" />
                              )}
                              {contractStatus.label} ({contractStatus.days} days)
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {customer.phone}
                      </span>
                      {customer.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {customer.email}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {getFullAddress(customer)}
                      </span>
                      {customer.contract_start_date && (
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          Contract: {customer.contract_start_date} to {customer.contract_end_date || 'ongoing'}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{customer.billing_plan === 'monthly' ? 'Monthly' : 'Per Cut'}</p>
                      <p className="font-bold text-lime-600">
                        ${(customer.billing_plan === 'monthly' ? customer.monthly_rate : customer.cost_per_service)?.toFixed(2)}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <Link to={`/admin/customers/${customer.id}`}>
                        <Button variant="ghost" size="icon" className="text-gray-500 hover:text-gray-700">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => handleEdit(customer)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(customer.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
